import { Ref, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button, ButtonProps } from '../../Shared/Button/Button';
import { ChangePlanDialog } from '../ChangePlanDialog/ChangePlan.dialog';
import { DowngradeToTierPlanDialog } from '../DowngradeToTierPlanDialog/DowngradeToTierPlanDialog';
import {
  SubscriptionPlanConfigDto,
  SubscriptionPlansDto,
} from '@/Domain/api/Subscription/dto/SubscriptionPlans.dto';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import {
  CHECKOUT_SOURCE_BILLING_PAGE,
  getUpgradeEntrypoint,
} from '@/Domain/components/Subscription/upgradeFunnelAnalytics';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useDraftSubscription } from '@/Domain/hooks/Subscription/useDraftSubscription.hook';
import { useUpdateSubscription } from '@/Domain/hooks/Subscription/useUpdateSubscription.hook';
import { StripeData } from '@/UI/Subscription/Subscription.page';

type Props = {
  tier: TierEnum;
  isBillingYearly: boolean;
  stripeData: StripeData;
  label: string;
  variant: ButtonProps['variant'];
  disabled: boolean;
  isDowngrade?: boolean;
  tooltip?: string;
  buttonRef?: Ref<HTMLButtonElement>;
};

function getStripePlan(
  tier: TierEnum,
  plans: SubscriptionPlansDto['products'],
): SubscriptionPlanConfigDto {
  const emptyStripePlan = {
    price_annually: '$0',
    price_monthly: '$0',
  };
  switch (tier) {
    case TierEnum.STARTER:
      return plans.starter ?? emptyStripePlan;
    case TierEnum.BASIC_2026:
      return plans.basic_2026 ?? emptyStripePlan;
    case TierEnum.ESSENTIAL_2026:
      return plans.essential_2026 ?? emptyStripePlan;
    case TierEnum.PROFESSIONAL_2026:
      return plans.professional_2026 ?? emptyStripePlan;
    case TierEnum.BUSINESS_2026:
      return plans.business_2026 ?? emptyStripePlan;
    default:
      return emptyStripePlan;
  }
}

export const ChoosePlanButton = ({
  tier,
  isBillingYearly,
  stripeData,
  label,
  variant,
  disabled,
  isDowngrade = false,
  tooltip,
  buttonRef,
}: Props) => {
  const { currentPlan, subscription, plans } = stripeData;
  const { capture } = usePostHogAnalytics();
  const [searchParams] = useSearchParams();
  const entrypoint = getUpgradeEntrypoint(searchParams);

  const [isChangePlanDialogOpen, setIsChangePlanDialogOpen] = useState(false);
  const [isChangeToTierPlanDialogOpen, setIsChangeToTierPlanDialogOpen] = useState(false);

  const interval = useMemo(
    () => (isBillingYearly ? SubscriptionIntervalEnum.Annually : SubscriptionIntervalEnum.Monthly),
    [isBillingYearly],
  );

  const updateSubscription = useUpdateSubscription({
    successCallback: () => setIsChangePlanDialogOpen(false),
    errorCallback: (error) => {
      setIsChangePlanDialogOpen(false);
      toast.error(SubscriptionHelper.getPlanChangeErrorMessage(error));
    },
    fromTier: currentPlan.tier,
    toTier: tier,
    interval,
    source: CHECKOUT_SOURCE_BILLING_PAGE,
    entrypoint,
  });

  const draftSubscription = useDraftSubscription();

  const priceId = isBillingYearly
    ? getStripePlan(tier, plans)?.price_annually
    : getStripePlan(tier, plans)?.price_monthly;

  const handleSelectPlan = () => {
    if (SubscriptionHelper.isFreeAccount(currentPlan.tier)) {
      return updateSubscription.handleUpdate(priceId);
    }

    setIsChangePlanDialogOpen(true);
    draftSubscription.handleDraft(priceId);
    return;
  };

  const handleSelectTierPlan = () => setIsChangeToTierPlanDialogOpen(true);

  // Free accounts go straight to Stripe; everyone else gets a dialog first.
  const outcome =
    tier === TierEnum.FREE
      ? 'downgrade_dialog'
      : tier === TierEnum.CUSTOM
        ? 'contact_sales'
        : SubscriptionHelper.isFreeAccount(currentPlan.tier)
          ? 'checkout'
          : 'change_plan_dialog';

  const handleClick = () => {
    capture(PostHogEventEnum.SubscriptionPlanSelected, {
      entrypoint,
      from_tier: currentPlan.tier,
      to_tier: tier,
      interval,
      price_id: priceId,
      outcome,
      is_downgrade: isDowngrade,
    });

    switch (tier) {
      case TierEnum.FREE:
        return handleSelectTierPlan();
      case TierEnum.CUSTOM:
        return window.open('https://www.quickchat.ai/contact', '_blank', 'noopener,noreferrer');
      default:
        return handleSelectPlan();
    }
  };

  const button = (
    <Button
      ref={buttonRef}
      variant={variant}
      className="w-full"
      onClick={handleClick}
      disabled={disabled}
      loading={updateSubscription.isPending}
      data-testid={`choose-plan-button-${tier}`}
    >
      {label}
    </Button>
  );

  return (
    <>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent className="max-w-xs" data-testid="choose-plan-tooltip">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}

      <DowngradeToTierPlanDialog
        currentPlan={currentPlan}
        canceled={subscription.canceled}
        isOpen={isChangeToTierPlanDialogOpen}
        onOpenChange={() => setIsChangeToTierPlanDialogOpen((prev) => !prev)}
      />

      <ChangePlanDialog
        interval={interval}
        nextTier={tier}
        currentPlan={currentPlan}
        canceled={subscription.canceled}
        isDowngrade={isDowngrade}
        data={draftSubscription.data}
        isOpen={isChangePlanDialogOpen}
        isPending={draftSubscription.isPending || updateSubscription.isPending}
        onOpenChange={() => {
          setIsChangePlanDialogOpen((prev) => !prev);
          draftSubscription.handleCancel();
        }}
        onCallback={() => updateSubscription.handleUpdate(priceId)}
      />
    </>
  );
};
